<script lang="ts">
    import { App, PRODUCT_VARIANT_DEFAULT_TITLE } from "@/constants";
    import { STATUS } from "@/constants/statuses";
    import {
        REVIEWS_WIDGET_NAMES,
        WISHLIST_WIDGET_NAMES,
    } from "@/constants/widget-names";
    import {
        customerInfoProviderContextToken,
        urlsBuilderContextToken,
    } from "@/context_tokens";
    import { ListingAverageReviewsWidgetDefinition } from "@apps/reviews/definitions";
    import { reviewsApplicationLoggerToken } from "@apps/reviews/tokens";
    import WishlistAddToCartButton from "@apps/wishlist/components/WishlistAddToCartButton.svelte";
    import WishlistItemQuantityChange from "@apps/wishlist/components/WishlistItemQuantityChange.svelte";
    import { BUTTON_VARIANT } from "@apps/wishlist/components/WishlistTheme/constants";
    import { wishlistThemeContextToken } from "@apps/wishlist/components/WishlistTheme/WIshlistThemeContextToken";
    import { WishlistItemEntity } from "@apps/wishlist/entities/WishlistItemEntity";
    import { WishlistWithItemsEntity } from "@apps/wishlist/entities/WishlistWithItemsEntity";
    import { WishlistProductStatusesQuery } from "@apps/wishlist/queries/WishlistProductStatusesQuery";
    import WishlistsBackInStockItemsQuery from "@apps/wishlist/queries/WishlistsBackInStockItemsQuery";
    import { WishlistsService } from "@apps/wishlist/services/WishlistsService";
    import WishlistItemAddedToCartSdkEvent from "@apps/wishlist/wishlists_sdk/events/WishlistItemAddedToCartSdkEvent";
    import { ProductIdFromStaticProvider } from "@lib/ProductIdManager/ProductIdFromStaticProvider";
    import { ProductIdManager } from "@lib/ProductIdManager/ProductIdManager";
    import { getTypedContext } from "@lib/svelte-typed-context";
    import { BaseModule } from "@modules/base_module/BaseModule";
    import { MODULE_STATUS } from "@modules/base_module/ModulesStatusesProvider/types";
    import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
    import { CombinedProductsAndProductsWithQuantityRulesQuery } from "@modules/products/queries/CombinedProductsAndProductsWithQuantityRulesQuery";
    import { ProductsQuery } from "@modules/products/queries/ProductsQuery";
    import { ProductsWithQuantityRulesQuery } from "@modules/products/queries/ProductsWithQuantityRulesQuery";
    import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
    import { dispatchSdkEvent } from "@modules/sdk/helpers";
    import { lastValueFrom, map } from "rxjs";
    import { createEventDispatcher } from "svelte";
    import { container } from "tsyringe";

    import ActionItem from "@components/Actions/ActionItem.svelte";
    import ActionItemLabel from "@components/Actions/ActionItemLabel.svelte";
    import ActionsList from "@components/Actions/ActionsList.svelte";
    import ActionsPopover from "@components/Actions/ActionsPopover.svelte";
    import { BOTTOM_RIGHT } from "@components/Actions/positions";
    import Badge from "@components/Badge/Badge.svelte";
    import BaseButton from "@components/Buttons/BaseButton.svelte";
    import Button from "@components/Buttons/Button.svelte";
    import ButtonWithIcon from "@components/Buttons/ButtonWithIcon.svelte";
    import Card from "@components/Card/Card.svelte";
    import DynamicImport from "@components/DynamicImport/DynamicImport.svelte";
    import Icon from "@components/Icon.svelte";
    import ThreedotVerticalIcon from "@components/icons/ThreedotVerticalIcon.svelte";
    import Image from "@components/Image/Image.svelte";
    import Money from "@components/Money.svelte";
    import T from "@components/T.svelte";
    import Widget from "@components/Widget.svelte";
    import WishlistIcon from "@components/WishlistIcon/WishlistIcon.svelte";

    import { wishlistPageWidgetContext } from "../WishlistPageWidgetContext";
    import WishlistPageItemSkeleton from "./WishlistPageItemSkeleton.svelte";

    export let item: WishlistItemEntity;
    export let wishlists: WishlistWithItemsEntity[];
    export let productsQuery: ProductsQuery;
    export let combinedProductsAndProductsWithQuantityRulesQuery: CombinedProductsAndProductsWithQuantityRulesQuery;
    export let productsWithQuantityRulesQuery: ProductsWithQuantityRulesQuery;
    export let wishlistsBackInStockItemsQuery: WishlistsBackInStockItemsQuery;
    export let wishlistProductStatusesQuery: WishlistProductStatusesQuery;
    export let isSharedItem: boolean;
    export let criticalNamespace: string;
    export let namespace: string;
    export let dynamicImportManager: DynamicImportManager;
    export let isCartAvailable: boolean;
    export let canEditWishlist: boolean;
    export let isEditProductQuantityEnabled: boolean;

    const dispatch = createEventDispatcher();

    const urlsBuilder = getTypedContext(urlsBuilderContextToken);

    const customerInfoProvider = getTypedContext(
        customerInfoProviderContextToken
    );

    const isB2b = customerInfoProvider.getIsB2b();

    const modulesStatusesProvider =
        container.resolve(BaseModule).exports.modulesStatusesProvider;
    const reviewsAppStatus$ = modulesStatusesProvider.selectModuleStatus(
        App.Reviews
    );

    const productIdManager = new ProductIdManager();
    productIdManager.registerProvider(
        new ProductIdFromStaticProvider(item.productId)
    );

    const targetWishlists = wishlists.filter((wishlist) =>
        wishlist.items.every(
            (currentItem) => currentItem.productId != item.productId
        )
    );

    const { cartQuery, cartService } = getTypedContext(
        wishlistPageWidgetContext
    );

    const wishlistsService = container.resolve(WishlistsService);

    let isQuantityUpdating = false;

    async function handleQuantityChange(
        event: CustomEvent<{ quantity: number }>
    ) {
        const newQuantity = event.detail.quantity;
        isQuantityUpdating = true;
        await lastValueFrom(
            wishlistsService.updateWishlistItemQuantity({
                wishlistId: item.wishlistId,
                productId: item.productId,
                variantId: item.variantId,
                quantity: newQuantity,
            })
        );
        isQuantityUpdating = false;
    }

    const productAndVariantInfo$ = productsQuery.selectProductAndVariant(
        item.productId,
        item.variantId
    );

    const combinedProductAndVariantInfo$ =
        combinedProductsAndProductsWithQuantityRulesQuery.selectProductAndVariant(
            item.productId,
            item.variantId
        );

    const productUrl$ = productAndVariantInfo$.pipe(
        map((productInfo) => {
            if (!productInfo.product) return null;

            return urlsBuilder.createProductUrl(
                productInfo.product.handle,
                productInfo.variant?.id
            );
        })
    );

    const imageUrl$ = productAndVariantInfo$.pipe(
        map((productAndVariantInfo) => {
            const [variantImage, productFeaturedImage, productFirstImage] = [
                productAndVariantInfo?.variant?.image?.url,
                productAndVariantInfo?.product?.featuredImage?.url,
                productAndVariantInfo?.product?.images[0].url,
            ];

            return variantImage || productFeaturedImage || productFirstImage;
        })
    );

    const productOptions$ = productAndVariantInfo$.pipe(
        map((productAndVariantInfo) => {
            const product = productAndVariantInfo.product;
            if (!product) return [];

            return product.options
                .filter(
                    (option) =>
                        !option.values.includes(
                            PRODUCT_VARIANT_DEFAULT_TITLE
                        ) && option.values.length > 1
                )
                .map((option) => ({
                    ...option,
                    variantsMap: option.values.reduce((acc, value) => {
                        const matchedVariant = product.variants.find(
                            (variant) =>
                                variant.options.some(
                                    (opt) =>
                                        opt.name === option.name &&
                                        opt.value === value
                                )
                        );
                        if (matchedVariant) {
                            acc[value] = matchedVariant.id;
                        }
                        return acc;
                    }, {} as Record<string, number>),
                }));
        })
    );

    const selectedOptions$ = productAndVariantInfo$.pipe(
        map((info) => {
            if (!info.product || !info.variant) return {};
            return info.variant.options.reduce((acc, option) => {
                acc[option.name] = option.value;
                return acc;
            }, {} as Record<string, string>);
        })
    );

    const isVariantInWishlist$ =
        wishlistProductStatusesQuery.selectVariantInWishlistStatus(
            item.productId,
            item.variantId
        );

    const soldOutStatus$ = productAndVariantInfo$.pipe(
        map((productAndVariantInfo) => {
            return !productAndVariantInfo.variant?.availableForSale;
        })
    );

    const isProductBackInStock$ =
        wishlistsBackInStockItemsQuery.selectIsProductBackInStock(
            item.productId,
            item.variantId
        );

    const isProductOnSale$ = productAndVariantInfo$.pipe(
        map((productAndVariantInfo) => {
            return !!(
                productAndVariantInfo.variant?.compareAtPrice &&
                productAndVariantInfo.variant?.compareAtPrice.amount >
                    productAndVariantInfo.variant?.price.amount
            );
        })
    );

    const handleToggleItem = () => {
        if ($isVariantInWishlist$) {
            dispatch("deleteItem");
        } else {
            dispatch("addItem");
        }
    };

    const handleMoveItem = (targetWishlistId: number) => {
        dispatch("moveItem", { targetWishlistId });
    };

    const itemInCartStatus$ = cartQuery
        .selectQuantityByVariantId(item.variantId)
        .pipe(map((quantity) => quantity > 0));

    const quantityRule$ = productsWithQuantityRulesQuery.selectQuantityRule(
        item.productId,
        item.variantId
    );

    const handleAddToCart = async () => {
        const minimumQuantity = $quantityRule$?.minimum ?? 1;
        const quantityToAdd = Math.max(minimumQuantity, item.quantity);

        await cartService.addToCart([
            {
                variantID: item.variantId,
                quantity: quantityToAdd,
            },
        ]);

        dispatchSdkEvent(
            new WishlistItemAddedToCartSdkEvent(
                WISHLIST_WIDGET_NAMES.WISHLIST_PAGE_WIDGET,
                item.productId,
                item.variantId
            )
        );
    };

    const handleListingAverageReviewsWidgetInit = () => {
        dispatchSdkEvent(
            new WidgetInjectedSdkEvent(
                REVIEWS_WIDGET_NAMES.LISTING_AVERAGE_REVIEWS_WIDGET
            )
        );
    };

    const addToCartLoadingStatus$ = cartQuery.selectEditItemStatus(
        item.variantId
    );

    const loading$ = addToCartLoadingStatus$.pipe(
        map((status) =>
            [STATUS.LOADING, STATUS.INITIAL_LOADING].includes(status)
        )
    );

    const wishlistBranding = getTypedContext(wishlistThemeContextToken);
</script>

{#if !$productAndVariantInfo$.product}
    <li>
        <Card>
            <WishlistPageItemSkeleton />
        </Card>
    </li>
{:else}
    <li
        class="gw-wl-page-item"
        data-gw-selector="wishlist-item"
        data-gw-product-id={item.productId}
        data-gw-variant-id={item.variantId}
        data-gw-wishlist-id={item.wishlistId}
        data-gw-product-tags={$productAndVariantInfo$.product?.tags.join(",") ||
            null}
    >
        <div class="gw-wl-page-item-actions">
            {#if !isSharedItem && wishlists.length > 1 && canEditWishlist}
                <ActionsPopover position={BOTTOM_RIGHT} autoClose>
                    <div slot="activator">
                        <ButtonWithIcon ariaLabel="Options">
                            <Icon color="#B1B5C3">
                                <ThreedotVerticalIcon />
                            </Icon>
                        </ButtonWithIcon>
                    </div>
                    <ActionsList>
                        <ActionItemLabel>
                            <T
                                {namespace}
                                translationKey="move_wishlist_item_label"
                            />
                        </ActionItemLabel>
                        {#each targetWishlists as wishlist}
                            <ActionItem
                                on:click={() => handleMoveItem(wishlist.id)}
                            >
                                {wishlist.title}
                            </ActionItem>
                        {/each}
                    </ActionsList>
                </ActionsPopover>
            {/if}
            {#if canEditWishlist}
                <BaseButton
                    class="gw-wl-page-item__delete-btn"
                    on:click={() => handleToggleItem()}
                >
                    <WishlistIcon
                        width={20}
                        height={20}
                        isFilled={$isVariantInWishlist$}
                        iconSettings={wishlistBranding.wishlistIconSettings}
                    />
                </BaseButton>
            {/if}
        </div>

        <a
            href={$productUrl$}
            class="gw-wl-page-item__image"
            aria-label={$productAndVariantInfo$.product.title}
        >
            <Image
                objectFit="cover"
                objectPosition="top"
                ratio={1}
                src={urlsBuilder.createProductImageUrl($imageUrl$ || "")}
                alt={$productAndVariantInfo$.product?.title || ""}
            />
            <div class="gw-wl-drawer-item-badge">
                {#if $isProductOnSale$}
                    <Badge
                        class="gw-wl-drawer-item-badge__on-sale"
                        variant="info"
                        textColor="#fff"
                        backgroundColor="#1A1A1A"
                    >
                        <T {namespace} translationKey="on_sale" />
                    </Badge>
                {/if}
                {#if $isProductBackInStock$}
                    <Badge
                        class="gw-wl-drawer-item-badge__back-in-stock"
                        variant="info"
                        textColor="#fff"
                        backgroundColor="#3772FF"
                    >
                        <T {namespace} translationKey="back_in_stock" />
                    </Badge>
                {/if}
            </div>
        </a>

        <div class="gw-wl-page-item-header">
            <a href={$productUrl$} class="gw-wl-page-item__title">
                {$productAndVariantInfo$.product?.title}
            </a>
        </div>

        <DynamicImport
            let:value={{
                module,
            }}
            definition={ListingAverageReviewsWidgetDefinition}
            shouldImportModule={$reviewsAppStatus$ ===
                MODULE_STATUS.INITIALIZED}
            {dynamicImportManager}
        >
            {#if module}
                <Widget
                    class="gw-wl-page-item__listing-avg-reviews-container"
                    widget={module}
                    params={[
                        {
                            isWidgetShadowRootEnabled: true,
                            showReviewsTooltip: false,
                        },
                        container.resolve(reviewsApplicationLoggerToken),
                        productIdManager,
                    ]}
                    on:widgetInit={handleListingAverageReviewsWidgetInit}
                />
            {/if}
        </DynamicImport>

        <div class="gw-wl-page-item-content__price-block">
            {#if $isProductOnSale$}
                <div class="gw-wl-page-item-compare-price">
                    <Money
                        amount={$combinedProductAndVariantInfo$?.variant
                            ?.compareAtPrice?.amount || 0}
                    />
                </div>
            {/if}

            <div class="gw-wl-page-item-price">
                <Money
                    amount={$combinedProductAndVariantInfo$?.variant?.price
                        .amount || 0}
                />
            </div>
        </div>

        <div class="gw-wl-page-item__footer">
            <slot
                name="options"
                productOptions={$productOptions$}
                selectedOptions={$selectedOptions$}
                productAndVariantInfo={$productAndVariantInfo$}
                {canEditWishlist}
            />

            {#if isB2b && isEditProductQuantityEnabled}
                <div class="gw-wl-page-item__quantity-change-wrapper">
                    <WishlistItemQuantityChange
                        quantity={item.quantity}
                        disabled={isQuantityUpdating || !canEditWishlist}
                        on:quantityChange={handleQuantityChange}
                    />
                </div>
            {/if}

            {#if isCartAvailable}
                <div class="gw-wl-page-item__cart-btn">
                    {#if !$soldOutStatus$ && !$itemInCartStatus$}
                        <WishlistAddToCartButton
                            on:addToCart={handleAddToCart}
                            loading={$loading$}
                            disabled={!canEditWishlist}
                            {namespace}
                            translationKey="add_to_cart"
                        />
                    {:else if $itemInCartStatus$}
                        <Button
                            block
                            url={urlsBuilder.createCartUrl()}
                            variant={BUTTON_VARIANT[
                                wishlistBranding.wishlistDrawerAndPageSettings
                                    .buttonsStyle
                            ]}
                            rounded
                        >
                            <T
                                namespace={criticalNamespace}
                                translationKey="show_in_cart_button_label"
                            />
                        </Button>
                    {:else if $soldOutStatus$ && !$itemInCartStatus$}
                        <Button
                            block
                            variant={BUTTON_VARIANT[
                                wishlistBranding.wishlistDrawerAndPageSettings
                                    .buttonsStyle
                            ]}
                            disabled
                            rounded
                        >
                            <T
                                namespace={criticalNamespace}
                                translationKey="sold_out_label"
                            />
                        </Button>
                    {/if}
                </div>
            {/if}
        </div>
    </li>
{/if}

<style lang="postcss">
    .gw-wl-page-item {
        position: relative;
        height: 100%;
        display: flex;
        flex-direction: column;
        box-sizing: border-box;
    }

    .gw-wl-page-item-actions {
        position: absolute;
        top: 0;
        left: 0;
        z-index: 1;
        width: 100%;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px;
    }
    .gw-wl-page-item__image {
        --gw-image-backdrop-color: transparent;
        position: relative;
    }

    :global(.gw-btn.gw-wl-page-item__delete-btn) {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        border: none;
        height: 30px;
        width: 30px;
        padding: 0;
        margin-left: auto;
        border-radius: 50%;
        background: var(--gw-wl-wishlistIconSettings-backgroundColor);
        cursor: pointer;
    }

    .gw-wl-drawer-item-badge {
        position: absolute;
        left: 8px;
        bottom: 8px;
        display: flex;
        gap: 5px;
        flex-direction: column;
        :global(.gw-badge) {
            width: fit-content;
            padding: 2px 8px;
            border-radius: 8px;
            line-height: 16px;
        }
    }

    .gw-wl-page-item-header {
        margin-top: 12px;
        display: flex;
        align-items: start;
        justify-content: space-between;
    }

    .gw-wl-page-item__title {
        display: -webkit-box;
        font-size: 0.875em;
        line-height: 1.5em;
        color: inherit;
        text-decoration: none;
        text-overflow: ellipsis;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    :global(.gw-wl-page-item__listing-avg-reviews-container) {
        margin-left: -2px;
    }

    .gw-wl-page-item-content__price-block {
        display: flex;
        gap: 4px;
        justify-content: flex-start;
        align-items: baseline;
        margin: 4px 0;
    }

    .gw-wl-page-item-compare-price {
        display: inline-block;
        font-size: 0.625em;
        line-height: 0.75em;
        text-decoration: line-through;
        color: var(--gw-lightgrey-text-color);
    }

    .gw-wl-page-item-price {
        font-size: 1em;
        font-weight: 500;
        line-height: 1em;
        display: inline-block;
    }

    .gw-wl-page-item__footer {
        margin-top: auto;
    }

    .gw-wl-page-item__cart-btn {
        display: flex;
        justify-content: center;
        margin-top: 8px;

        & :global(.gw-btn.gw-button.--outlined) {
            color: var(--gw-wl-wishlistDrawerAndPageSettings-buttonsFontColor);
        }

        .gw-wl-page-item:not(:has(.gw-wl-page-item__quantity-change-wrapper))
            & {
            margin-top: auto;
        }
    }

    .gw-wl-page-item__quantity-change-wrapper {
        margin: auto 0 0.6em 0;
    }
</style>
